import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import type { EventView } from '../events/labels';
import { FUNCTIONS_RESOURCE } from '../people/types';
import { AnswersBadge } from '../requests/AnswersBadge';
import { ConfirmCrewDialog } from '../requests/ConfirmCrewDialog';
import { SendRequestDialog } from '../requests/SendRequestDialog';
import { REQUESTS_KEY, REQUESTS_RESOURCE, uniquePeople, type ConfirmationSent, type Recipient } from '../requests/types';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { useCrewQuote } from './CrewQuotePrompt';
import { PositionsPanel } from './PositionsPanel';
import { refreshCrew } from './refreshCrew';
import { CREW_KEY, CREW_RESOURCE, type Assignment } from './types';

function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

export type CrewData = { assignments: Assignment[]; unstaffed?: boolean; unstaffedDays?: string[]; unstaffedManual?: boolean; datesChanged?: boolean };
export type Sending = { added?: { personId: string; personName: string }; afterAdd: boolean; functionId?: string };

/**
 * A request from a free place (Obsada's „Wyślij zapytanie”, the grid panel's „Wyślij zapytanie do wolnych miejsc”):
 * the place's function is preset only for whoever may see functions — the dialog shows it only then (review I1).
 */
export const askPreset = (mayAskForPlace: boolean, functionId: string | null | undefined): Sending => ({ afterAdd: false, ...(mayAskForPlace && functionId ? { functionId } : {}) });

/**
 * One event's crew as Obsada shows it — shared by the event's „Obsada” tab and the side panel of „Dostępność → Ludzie”
 * (owner 2026-10-26 07:49, point 2: „the same components”, no second copy): the crew and the answers to the questions,
 * the quote's green bar (`crewQuote`), the places with ⓘ Info (`positions`), and the dialogs of „Wyślij zapytanie”,
 * „Wyślij potwierdzenie do obsady” and „Usuń z obsady” (`dialogs`). Every crew change goes through `refreshCrew`.
 */
export function useCrewEvent(event: EventView) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const crew = useQuery({ queryKey: [...CREW_KEY, 'event', event.id], queryFn: () => api<CrewData>(`/api/crew/events/${event.id}/assignments`) });
  const refresh = () => refreshCrew(queryClient);
  // owner 18:07 („pytaj zamiast automatu”): every crew change asks about the quote; the difference stays as a notice
  const crewQuote = useCrewQuote(event.id);
  const changed = async () => {
    await refresh();
    await crewQuote.ask();
  };
  // availability requests (F20): the answers beside each person, and the dialog to send one
  const mayViewRequests = !!me && can(me, REQUESTS_RESOURCE, 'view');
  const maySend = !!me && can(me, REQUESTS_RESOURCE, 'create');
  const requests = useQuery({
    queryKey: [...REQUESTS_KEY, 'event', event.id],
    enabled: mayViewRequests,
    queryFn: () => api<{ recipients: Recipient[]; confirmations?: ConfirmationSent[] }>(`/api/requests/events/${event.id}`),
  });
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState<Sending | null>(null);
  const [removing, setRemoving] = useState<Assignment | null>(null);
  // "Wyślij zapytanie" on an open place presets the place's function, which the dialog shows only with the right to see functions
  const mayAskForPlace = maySend && !!me && can(me, FUNCTIONS_RESOURCE, 'view');
  const mayDelete = !!me && can(me, CREW_RESOURCE, 'delete');
  const remove = useMutation({
    mutationFn: (a: Assignment) => api(`/api/crew/assignments/${a.id}?version=${a.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await changed();
    },
  });
  const closeRemove = () => {
    setRemoving(null);
    remove.reset();
  };

  const list = crew.data?.assignments ?? [];
  const asked = requests.data?.recipients ?? [];
  const askedOf = new Map(asked.map((r) => [r.personId, r]));
  const crewPeople = uniquePeople(list);
  const inCrew = new Set(crewPeople.map((p) => p.personId));
  const name = event.name || 'Wydarzenie';

  /**
   * Owner 2026-10-27 12:26: the confirmation goes to „Potwierdzeni” only — with nobody confirmed the button says so
   * (`title`) and is off. Rule 7a: without the status the dialog itself says it (the server's list).
   */
  // review M8: as the server — „Potwierdzony” with at least one active day
  const goes = (a: Assignment) => a.status === 'potwierdzony' && (a.days === undefined || a.days.some((d) => d.active));
  const confirmBlocked = list.some((a) => a.status !== undefined) && !list.some(goes) ? 'Nikt z obsady nie jest jeszcze potwierdzony' : null;

  /** „Wyślij zapytanie” for free places of `functionId` (preset only when allowed, see `askPreset`). */
  const askFor = (functionId?: string | null) => setSending(askPreset(mayAskForPlace, functionId));

  /**
   * The places with ⓘ Info; `ask` false: no „Wyślij zapytanie” beside a free place (Obsada while adding); `infoFilled`:
   * Info with the filled places per function (the grid's side panel, owner 2026-10-27 08:16).
   */
  const positions = (ask = true, infoFilled = false): ReactNode => (
    <PositionsPanel
      event={event}
      infoFilled={infoFilled}
      assignments={list}
      onChanged={() => void crewQuote.ask()}
      badge={(personId: string) => (askedOf.has(personId) ? <AnswersBadge recipient={askedOf.get(personId)!} /> : null)}
      {...(mayDelete ? { onRemove: (a: Assignment) => setRemoving(a) } : {})}
      {...(mayAskForPlace && ask ? { onAsk: (functionId: string) => askFor(functionId) } : {})}
    />
  );

  const dialogs: ReactNode = (
    <>
      {sending && (
        <SendRequestDialog
          event={event}
          people={sending.added && !inCrew.has(sending.added.personId) ? [...crewPeople, sending.added] : crewPeople}
          asked={asked}
          {...(sending.added ? { initialPersonIds: [sending.added.personId] } : {})}
          {...(sending.functionId ? { initialFunctionId: sending.functionId } : {})}
          afterAdd={sending.afterAdd}
          onClose={() => {
            const after = sending.afterAdd;
            setSending(null);
            if (after) void crewQuote.ask();
          }}
        />
      )}
      {confirming && <ConfirmCrewDialog eventId={event.id} eventName={name} onClose={() => setConfirming(false)} />}
      {crewQuote.dialog}
      {removing && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="crew-remove-title">
            <DialogLayer onClose={closeRemove} busy={remove.isPending} />
            <h2 id="crew-remove-title">Usunąć z obsady?</h2>
            <p>
              {removing.personName ?? 'Osoba'}
              {removing.functionName ? ` (${removing.functionName})` : ''} zniknie z obsady wydarzenia „{name}”.
            </p>
            {remove.error instanceof ApiError && remove.error.status === 409 && (
              <ConflictNotice
                onReload={() => {
                  closeRemove();
                  void refresh();
                }}
              />
            )}
            {remove.error && !(remove.error instanceof ApiError && remove.error.status === 409) && (
              <p className="alert" role="alert">
                {errorText(remove.error, 'Nie udało się usunąć')}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate(removing)}>
                {remove.isPending ? 'Usuwanie…' : 'Usuń z obsady'}
              </button>
              <button className="btn" type="button" onClick={closeRemove}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // the grid's event panel (variant A, owner 2026-10-28): „Usuń z obsady” from a row's „⋯” — the same confirmation
  const askRemove = (a: Assignment) => setRemoving(a);
  return { crew, list, refresh, crewQuote, requests, asked, askedOf, crewPeople, inCrew, maySend, mayDelete, setSending, setConfirming, confirmBlocked, askFor, positions, dialogs, askRemove, goes };
}
